import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import {
  getCookie,
  removeCookie,
  setLocalStorage,
  localStorageMethod,
  decryptEncryptedJWT
} from 'src/common/config/FunctionHelper';
import { getSubscriptionDetailsStripe } from 'src/common/Scraper';
import { setUserEmail, setIsLoadingUserDetails } from 'src/redux/counterSlice';
import { setStateStripeSubscription } from 'src/redux/userReducer';
import { setStripeLoader } from 'src/redux/siteAuditReducer';
import { paths } from 'src/common/config/TextMessage';

/**
 * Paths where the `reaudit_process` localStorage flag should be cleared.
 * Strip the leading `/` to match the substring comparison used in the original.
 */
const RE_AUDIT_CLEANUP_PATHS = new Set([
  paths.dashboard,
  paths.siteAudit,
  paths.billing,
  paths.userManagement,
  paths.seoTasks,
  paths.externalScans,
  paths.price,
  paths.plan,
  paths.keywordResearch,
  paths.leadGeneration
]);

interface UseAppInitializerOptions {
  /** Stable logout callback returned by `useLogout`. */
  logout: () => Promise<void>;
}

/**
 * Handles application-level auth and data initialization:
 * - Removes stale or falsy auth cookies on mount
 * - Decodes the JWT and derives `userId` (used to trigger the user-details query)
 * - Fetches Stripe subscription details when not yet loaded
 * - Syncs `userEmail` in Redux after every navigation
 * - Clears the `reaudit_process` flag when the user lands on relevant pages
 *
 * @returns `{ userId }` — needed to gate the `/me` user-details API call.
 */
export const useAppInitializer = ({ logout }: UseAppInitializerOptions) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const subscription = useSelector((state: RootState) => state.user.subscription);
  const [userId, setUserId] = useState<string | null>(null);

  // ── Remove stale / falsy auth cookie on mount ────────────────────────────
  useEffect(() => {
    const cookie = getCookie(process.env.REACT_APP_COOKIE_NAME);
    if (!cookie || cookie === 'false') removeCookie();
  }, []);

  // ── Fetch auth + subscription on every navigation ────────────────────────
  useEffect(() => {
    const { pathname, search } = location;
    const isSuccess = pathname.includes('success');
    const sessionId = new URLSearchParams(search).get('session_id');

    const fetchAuthAndSubscription = async () => {
      try {
        const encodedJWT = getCookie(process.env.REACT_APP_COOKIE_NAME);
        const jwtDecode: any = await decryptEncryptedJWT(encodedJWT);
        const fetchedUserId = jwtDecode?.userId;

        if (fetchedUserId) setUserId(fetchedUserId);

        if (fetchedUserId && !subscription) {
          dispatch(setStripeLoader(true));
          try {
            const details = await getSubscriptionDetailsStripe();
            dispatch(setStateStripeSubscription(details || {}));
          } catch (error) {
            console.error('Error fetching subscription details:', error);
          } finally {
            dispatch(setStripeLoader(false));
          }
        }
      } catch {
        dispatch(setIsLoadingUserDetails(false));
        dispatch(setStripeLoader(false));
        if (getCookie(process.env.REACT_APP_COOKIE_NAME)) logout();
      }
    };

    if (!isSuccess && !sessionId) {
      fetchAuthAndSubscription();
    }

    // Sync userEmail in Redux with the latest JWT
    const cookieToken = getCookie(process.env.REACT_APP_COOKIE_NAME);
    if (cookieToken && cookieToken !== 'false') {
      decryptEncryptedJWT(cookieToken).then((jwt: any) => {
        if (jwt) {
          dispatch(setUserEmail(jwt.email));
          setLocalStorage(cookieToken);
        }
      });
    }
  }, [location, dispatch, subscription, logout]);

  // ── Clear re-audit flag on relevant pages ────────────────────────────────
  useEffect(() => {
    if (
      RE_AUDIT_CLEANUP_PATHS.has(location.pathname) &&
      localStorageMethod('get', 'reaudit_process') === 'true'
    ) {
      localStorageMethod('remove', 'reaudit_process');
    }
  }, [location.pathname]);

  return { userId };
};
